import { useState, useEffect, useCallback, useRef } from "react"
import type { CurrencyRate } from "lib/types/generated/graphql-types"
import GET_CURRENCY_RATES from "../../../graphql/queries/currencies/GetCurrencyRates"
import { useLazyQuery } from "@apollo/client"

/**
 * Hook for fetching and managing currency conversion rates
 *
 * @param baseCurrencies Array of base currency codes to fetch rates for
 * @param filterCurrencies Array of target currency codes to filter results
 * @returns Object containing rates map, loading state, and error
 */
export function useCurrencyRates(baseCurrencies: string[] = [], filterCurrencies: string[] = []) {
  const [ratesMap, setRatesMap] = useState<Record<string, Record<string, number>>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [fetchedCurrencies, setFetchedCurrencies] = useState<Set<string>>(new Set())

  const baseCurrenciesRef = useRef(baseCurrencies)
  const filterCurrenciesRef = useRef(filterCurrencies)
  const fetchedCurrenciesRef = useRef(fetchedCurrencies)

  useEffect(() => {
    baseCurrenciesRef.current = baseCurrencies
    filterCurrenciesRef.current = filterCurrencies
  }, [baseCurrencies, filterCurrencies])

  useEffect(() => {
    fetchedCurrenciesRef.current = fetchedCurrencies
  }, [fetchedCurrencies])

  const isMounted = useRef(true)
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const [getCurrencyRates, { loading: queryLoading }] = useLazyQuery(GET_CURRENCY_RATES, {
    onError: (err) => {
      if (isMounted.current) {
        setError(new Error(`Failed to fetch currency rates: ${err.message}`))
      }
    },
  })

  const fetchRatesForCurrency = useCallback(async (baseCurrency: string) => {
    if (!isMounted.current) return false

    try {
      const result = await getCurrencyRates({
        variables: {
          baseCurrencyCode: baseCurrency,
          filterCurrencies: filterCurrenciesRef.current,
        },
      })

      if (!result.data?.getCurrencyRates?.rates) {
        console.error(`Error fetching rates for ${baseCurrency}: No rates returned`)
        return false
      }

      const currencyRates = Object.fromEntries(
        result.data.getCurrencyRates.rates.map((i: CurrencyRate) => [i.currencyCode, i.rate]),
      )

      if (isMounted.current) {
        setRatesMap((prev) => ({
          ...prev,
          [baseCurrency]: currencyRates,
        }))

        setFetchedCurrencies((prev) => {
          const newSet = new Set([...prev, baseCurrency])
          fetchedCurrenciesRef.current = newSet
          return newSet
        })
      }

      return true
    } catch (err) {
      console.error(`Error fetching rates for ${baseCurrency}:`, err)
      return false
    }
  }, [getCurrencyRates])

  useEffect(() => {
    const currenciesToFetch = baseCurrencies.filter(
      (currency) => !fetchedCurrenciesRef.current.has(currency)
    )

    if (currenciesToFetch.length === 0) return

    let isEffectActive = true
    setLoading(true)
    setError(null)

    const fetchAllRates = async () => {
      try {
        const results = await Promise.all(
          currenciesToFetch.map((currency) => fetchRatesForCurrency(currency))
        )

        if (isEffectActive && isMounted.current) {
          if (results.some((result) => !result)) {
            setError(new Error("Failed to fetch some currency rates"))
          }
          setLoading(false)
        }
      } catch (err) {
        if (isEffectActive && isMounted.current) {
          setError(err instanceof Error ? err : new Error("Failed to fetch currency rates"))
          setLoading(false)
        }
      }
    }

    fetchAllRates()

    return () => {
      isEffectActive = false
    }
  }, [baseCurrencies, fetchRatesForCurrency])

  useEffect(() => {
    if (queryLoading && isMounted.current) {
      setLoading(true)
    }
  }, [queryLoading])

  return {
    ratesMap,
    loading,
    error,
    fetchedCurrencies: Array.from(fetchedCurrencies),
  }
}
